import type { CampaignTimeslotItem } from '~/api/endpoints/getControls'

/** Campaigns C3: the exact dim written on facts carrying the campaign, and the managed dims
 *  that attribution maps onto (uniform across facts — the slot positions differ per fact but
 *  v_dim_catalog names are shared). The predicate is derived per fact at walk-build; the URL
 *  carries identity only; explicit dims are legacy/edited forms. */
export const CAMPAIGN_MANAGED_DIMS = [
  'Topic Level 3',
  'Topic Level 4',
  'Brand Casting Layer Level 1',
] as const

export const CAMPAIGN_EXACT_DIM = 'Campaign'
export const ALL_CAMPAIGN_DIMS: readonly string[] = [...CAMPAIGN_MANAGED_DIMS, CAMPAIGN_EXACT_DIM]

const ATTR_TO_DIM: ReadonlyArray<[keyof CampaignTimeslotItem, string]> = [
  ['topic_l3', 'Topic Level 3'],
  ['topic_l4', 'Topic Level 4'],
  ['brand_casting_layer_l1', 'Brand Casting Layer Level 1'],
]

/** Non-null attribution -> single-value dim filters. Empty object = window-only campaign. */
export function predicateOf(item: CampaignTimeslotItem): Record<string, string[]> {
  const out: Record<string, string[]> = {}
  for (const [attr, dim] of ATTR_TO_DIM) {
    const v = item[attr]
    if (typeof v === 'string' && v.length > 0) out[dim] = [v]
  }
  return out
}

/** C3 (spec D-C3-1): the fact-aware resolver — THE one rule. Exact dim on carrying facts,
 *  attribution filters elsewhere. Never both. */
export function resolvedCampaignFilters(
  item: CampaignTimeslotItem,
  factCarriesCampaign: boolean,
): Record<string, string[]> {
  return factCarriesCampaign ? { [CAMPAIGN_EXACT_DIM]: [item.value] } : predicateOf(item)
}

/** C3b (spec D-C3b-1): the ratio variant of the one rule. A ratio is a two-fact computation —
 *  exact requires BOTH component facts to carry the dim; anything less resolves attribution
 *  on both sides. Never mixed (one-sided exact = the D0 wrong-number defect). */
export function resolvedRatioCampaignFilters(
  item: CampaignTimeslotItem,
  numCarriesCampaign: boolean,
  denomCarriesCampaign: boolean,
): Record<string, string[]> {
  return numCarriesCampaign && denomCarriesCampaign
    ? { [CAMPAIGN_EXACT_DIM]: [item.value] }
    : predicateOf(item)
}

/** The atomic selection write (spec §4): identity + custom window + ALL campaign dim keys
 *  (null-clear, clearing a previous campaign's leftovers; non-campaign filters are untouched
 *  by construction — their keys never appear here). */
export function buildCampaignSelectionParams(
  item: CampaignTimeslotItem,
): Record<string, string | null> {
  const params: Record<string, string | null> = {
    campaign: item.value,
    tf_preset: 'custom',
    from: item.from!,
    to: item.to!,
  }
  for (const dim of ALL_CAMPAIGN_DIMS) params[`dim.${dim}`] = null
  return params
}

/** Reconciliation check (spec §4): identity holds iff the custom window matches the campaign's
 *  AND dims follow the dual-form rule (C3 pure-identity: all absent; C1 legacy: managed dims
 *  exactly predicateOf; either form with explicit dim.Campaign = [item.value] is redundant,
 *  not divergent). Non-campaign filters never affect the verdict. */
export function campaignConsistent(
  item: CampaignTimeslotItem,
  urlFilters: Record<string, string[]>,
  tfPreset: string,
  fromIso: string,
  toIso: string,
): boolean {
  if (tfPreset !== 'custom' || fromIso !== item.from || toIso !== item.to) return false
  // Explicit dim.Campaign: absent or exactly [item.value] — anything else is divergence.
  const exact = urlFilters[CAMPAIGN_EXACT_DIM] ?? []
  if (exact.length > 0 && (exact.length !== 1 || exact[0] !== item.value)) return false
  // Managed dims: EITHER all absent (C3 pure-identity form) OR exactly predicateOf (legacy
  // C1 bookmarks/pins keep their identity). Any other combination = user edit = divergence.
  const pred = predicateOf(item)
  const allAbsent = CAMPAIGN_MANAGED_DIMS.every((d) => (urlFilters[d] ?? []).length === 0)
  if (allAbsent) return true
  for (const dim of CAMPAIGN_MANAGED_DIMS) {
    const want = pred[dim] ?? []
    const have = urlFilters[dim] ?? []
    if (want.length !== have.length || want[0] !== have[0]) return false
  }
  return true
}

/** Per-arm merge (spec §6): a campaign arm REPLACES all campaign-owned dims (exact + managed)
 *  with its resolved filters and inherits every other filter. An empty resolved filters means
 *  "not a campaign arm" — base unchanged. */
export function mergeArmFilters(
  base: Record<string, string[]>,
  resolved: Record<string, string[]>,
): Record<string, string[]> {
  if (Object.keys(resolved).length === 0) return base
  const out: Record<string, string[]> = {}
  for (const [k, v] of Object.entries(base)) {
    if (!ALL_CAMPAIGN_DIMS.includes(k)) out[k] = v
  }
  for (const [k, v] of Object.entries(resolved)) out[k] = v
  return out
}
